import { faCirclePlus } from "@fortawesome/free-solid-svg-icons/faCirclePlus";
import { faPenToSquare } from "@fortawesome/free-solid-svg-icons/faPenToSquare";
import { faTimesCircle } from "@fortawesome/free-solid-svg-icons/faTimesCircle";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IEndereco } from "app/types/IEndereco";
import clsx from "clsx";
import React, { memo, useCallback, useEffect, useMemo, useState } from "react";

const estilosBotoes = require("../../../../estilos/botoes.module.scss").default;
const estiloAbaEnderecos = require("./aba-enderecos.module.scss").default;
const estiloTabela = require("../../../../estilos/tabelas.module.scss").default;

// Header estático — criado uma única vez no módulo (#27)
const _headerCells = ["Classificações", "Endereço", "Número", "Complemento", "Bairro", "Cidade", "UF", "Cep", "Referência", ""].map((nome, index) => (
  <th key={`AbaEnderecos_header_${index}`}>
    {nome}
  </th>
));

const useRenderizarLinhasAposEntrada = () => {
  const [renderizarLinhas, setRenderizarLinhas] = useState(false);

  useEffect(() => {
    if (typeof window !== "undefined" && window.requestAnimationFrame) {
      const frame = window.requestAnimationFrame(() => setRenderizarLinhas(true));
      return () => window.cancelAnimationFrame(frame);
    }

    const timeout = setTimeout(() => setRenderizarLinhas(true), 0);
    return () => clearTimeout(timeout);
  }, []);

  return renderizarLinhas;
};

// Linha extraída como componente memo no escopo do módulo (#23)
// Só re-renderiza quando os dados dessa linha específica mudarem
type LinhaEnderecoProps = {
  endereco: IEndereco;
  index: number;
  selecionado: boolean;
  corSelecionado?: string;
  textoSelecionado?: string;
  onToggle: (endereco: IEndereco) => void;
  onEditar: (endereco: IEndereco) => void;
};

const LinhaEndereco = memo(({ endereco, index, selecionado, corSelecionado, textoSelecionado, onToggle, onEditar }: LinhaEnderecoProps) => (
  <tr
    id={`AbaEnderecos_row_${endereco.Codigo}_Index_${index}`}
    className={selecionado ? estiloTabela.linhaSelecionada : estiloTabela.linhaClicavel}
    style={selecionado ? { backgroundColor: corSelecionado, color: textoSelecionado } : {}}
    onClick={() => onToggle(endereco)}
  >
    <td id={`AbaEndereco_DescricaoClassificacoes_${index}`}>{endereco?.DescricaoClassificacoes}</td>
    <td id={`AbaEndereco_Logradouro_${index}`}>{endereco?.Logradouro?.toUpperCase()}</td>
    <td id={`AbaEndereco_Numero_${index}`}>{endereco?.Numero}</td>
    <td id={`AbaEndereco_Complemento_${index}`}>{endereco?.Complemento?.toUpperCase()}</td>
    <td id={`AbaEndereco_Bairro_${index}`}>{endereco?.Bairro?.toUpperCase()}</td>
    <td id={`AbaEndereco_Cidade_${index}`}>{endereco?.NomeCidade?.toUpperCase()}</td>
    <td id={`AbaEndereco_Uf_${index}`}>{endereco?.SiglaUf?.toUpperCase()}</td>
    <td id={`AbaEndereco_Cep_${index}`}>{endereco?.Cep}</td>
    <td id={`AbaEndereco_Referencia_${index}`}>{endereco?.Referencia}</td>
    <td id={`AbaEndereco_Editar_${index}`}>
      {endereco.PermitirEditar && (
        <FontAwesomeIcon
          id={`AbaEnderecos_btnEditar_${endereco.Codigo}_${index}`}
          className="_m-1"
          icon={faPenToSquare}
          color={"#3c8ac9"}
          fontSize={20}
          onClick={(e) => {
            onEditar(endereco);
            e.stopPropagation();
          }}
        />
      )}
    </td>
  </tr>
));

export type AbaEnderecosProps = {
  tipoPessoa: string // 441453
  enderecos: IEndereco[];
  enderecosSelecionados: IEndereco[];
  exibitExcluirEndereco: boolean
  habilitarExcluirEndereco: boolean
  corSelecionado?: string;
  textoSelecionado?: string;
  onIncluirEndereco: () => void;
  onExcluirEndereco: (enderecos: IEndereco[]) => void;
  onEditarEndereco: (endereco: IEndereco) => void;
  onToggleEnderecoSelecionado: (enderecos: IEndereco) => void;
};

export const AbaEnderecos = memo((props: AbaEnderecosProps) => {
  const renderizarLinhas = useRenderizarLinhasAposEntrada();

  // Set para lookup O(1) em vez de Array.includes() O(n) por linha (#perf)
  const enderecosSelecionadosSet = useMemo(
    () => renderizarLinhas ? new Set(props.enderecosSelecionados) : new Set<IEndereco>(),
    [renderizarLinhas, props.enderecosSelecionados]
  );

  const handleToggle = useCallback((endereco: IEndereco) => {
    props.onToggleEnderecoSelecionado(endereco);
  }, [props.onToggleEnderecoSelecionado]);

  const handleEditar = useCallback((endereco: IEndereco) => {
    props.onEditarEndereco(endereco);
  }, [props.onEditarEndereco]);

  const handleExcluir = useCallback(() => {
    props.onExcluirEndereco(props.enderecosSelecionados);
  }, [props.onExcluirEndereco, props.enderecosSelecionados]);

  return (
    <>
      <div className="_pt-5 _mb-2 _flex">
        <button
          id="AbaEnderecos_btnIncluir"
          className={clsx('no-global-button _p-3', estilosBotoes.botaoIcone, estilosBotoes.botaoIconeVerde)}
          onClick={props.onIncluirEndereco}
        >
          <FontAwesomeIcon icon={faCirclePlus} color={"#5ac93c"} fontSize={20} />
          Incluir
        </button>

        {props.exibitExcluirEndereco && (
          <button
            id="AbaEnderecos_btnExcluir"
            className={clsx('no-global-button _p-3', estilosBotoes.botaoIcone, estilosBotoes.botaoIconeVermelho)}
            disabled={!props.habilitarExcluirEndereco}
            onClick={handleExcluir}
          >
            <FontAwesomeIcon icon={faTimesCircle} color={"#f25713"} fontSize={20} />
            Excluir
          </button>
        )}
      </div>

      <div className={estiloTabela.containerRolavel}>
        <table className={`_w-full ${estiloTabela.tabela} ${estiloAbaEnderecos.tabelaEnderecoComissoes}`}>
          <thead>
            <tr>
              {_headerCells}
            </tr>
          </thead>
          <tbody>
            {renderizarLinhas && props.enderecos?.map((endereco, index) => (
              <LinhaEndereco
                key={`${endereco.Codigo}_${index}`}
                endereco={endereco}
                index={index}
                selecionado={enderecosSelecionadosSet.has(endereco)}
                corSelecionado={props.corSelecionado}
                textoSelecionado={props.textoSelecionado}
                onToggle={handleToggle}
                onEditar={handleEditar}
              />
            ))}
          </tbody>
        </table>
      </div>
      {<section style={{ marginBottom: "100px" }} />}
    </>
  );
});
